import React, { useState } from 'react'
import { css } from '@emotion/core'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import qs from 'qs'
import { Checkbox } from '@/components/atoms/checkbox'
import { tableBodyTdStyle } from '@/constants/layout'
import { guestNameKanaStyle, guestNameStyle } from '@/components/pages/accommodation-management/list-layout'
import { ReservationTask, ReservationTaskChange, ReservationTaskStaff } from '@/models/reservation/reservation-task'
import { ReservationTaskStatusLabel } from './reservation-task-status-label'
import { HandlingStatusSelect } from './handling-status-select'
import { TaskStaffSelect } from './task-staff-select'
import { ReservationTaskChangeDetail } from './reservation-task-change-detail'

type Props = {
  task: ReservationTask
  isSelected: boolean
  staffOptions: ReservationTaskStaff[]
  isReadOnly: boolean
  onToggle: (taskId: number, checked: boolean) => void
  onUpdate: (taskIds: number[], change: ReservationTaskChange) => void
}

export const ReservationTaskRow: React.FC<Props> = ({ task, isSelected, staffOptions, isReadOnly, onToggle, onUpdate }) => {
  const { t } = useTranslation()
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const detailId = `reservation-task-change-detail-${task.id}`
  // 予約詳細はクエリの reservationId で予約を取得し、チェックイン済みなら自動でチェックイン以降の詳細に切り替える
  const detailLocation = {
    pathname: `/reservation/${task.reservationUuid}`,
    search: qs.stringify({ reservationId: task.reservationId }),
  }
  // 行全体をリンクにすると、行内のプルダウンのメニュー（body 直下に描画される）のクリックも React の親子関係をたどって
  // リンクに届き、遷移してしまう。そのため遷移するセルの中にだけリンクを置く。
  // クリックできる範囲を行の見た目にそろえるため、セルの余白ごとリンクにする。
  // リンクは flex で縦中央に揃えるので、複数行の内容が横に並ばないよう内側を div で包む
  const linkCell = (content: React.ReactNode) => (
    <td css={[tableBodyTdStyle, linkCellStyle]}>
      <Link to={detailLocation} css={cellLinkStyle}>
        <div css={cellContentStyle}>{content}</div>
      </Link>
    </td>
  )

  return (
    <>
      <tr css={rowStyle}>
        <td css={[tableBodyTdStyle, checkboxCellStyle]}>
          <Checkbox value={isSelected} disabled={isReadOnly} onChange={checked => onToggle(task.id, checked)} />
        </td>
        <td css={[tableBodyTdStyle, expandCellStyle]}>
          {task.changeSummary && (
            <button
              type="button"
              aria-expanded={isDetailOpen}
              aria-controls={isDetailOpen ? detailId : undefined}
              aria-label={t('reservationTask.ToggleChangeDetail')}
              onClick={() => setIsDetailOpen(!isDetailOpen)}
              css={expandButtonStyle}
            >
              <img
                src={require('@/static/images/arrow_right.svg')}
                alt=""
                aria-hidden
                css={[expandIconStyle, isDetailOpen ? expandIconOpenStyle : expandIconClosedStyle]}
              />
            </button>
          )}
        </td>
        {linkCell(<ReservationTaskStatusLabel status={task.reservationStatus} />)}
        {linkCell(dayjs(task.reservationUpdatedAt).format('M/D HH:mm'))}
        {linkCell(task.checkinDate ? `${dayjs(task.checkinDate).format('YYYY/M/D')} ${task.nights}${t('Night')}` : '-')}
        {linkCell(
          task.rooms.length
            ? task.rooms.map((room, index) => (
                <div key={index}>
                  {room.roomTypeName} {t('reservationTask.RoomCount', { count: room.roomCount })}
                </div>
              ))
            : '-',
        )}
        {linkCell(
          <>
            <div css={[guestNameKanaStyle, guestNameTextStyle]}>{task.guestNameKana ?? '-'}</div>
            <div css={[guestNameStyle, guestNameTextStyle]}>{task.guestName ?? '-'}</div>
          </>,
        )}
        {/* 要約文は API 由来なので、HTML ではなく文字列として描画する（規約 E-5） */}
        {linkCell(<div css={summaryStyle}>{task.changeSummary?.summary ?? '-'}</div>)}
        <td css={tableBodyTdStyle}>
          <HandlingStatusSelect
            value={task.handlingStatus}
            disabled={isReadOnly}
            onChange={handlingStatus => onUpdate([task.id], { handlingStatus })}
          />
        </td>
        <td css={tableBodyTdStyle}>
          <TaskStaffSelect
            value={task.staff}
            options={staffOptions}
            disabled={isReadOnly}
            onChange={staffId => onUpdate([task.id], { staffId })}
          />
        </td>
      </tr>
      {isDetailOpen && task.changeSummary && (
        <tr>
          <td id={detailId} colSpan={10} css={[tableBodyTdStyle, detailCellStyle]}>
            <ReservationTaskChangeDetail changeSummary={task.changeSummary} />
          </td>
        </tr>
      )}
    </>
  )
}

const rowStyle = css({
  background: '#FFFFFF',
  '&:hover': { background: '#FAFAFA' },
})

const checkboxCellStyle = css({
  paddingLeft: 32,
})

const expandCellStyle = css({
  padding: 0,
  textAlign: 'center',
})

const expandButtonStyle = css({
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  lineHeight: 0,
  verticalAlign: 'middle',
})

const expandIconStyle = css({
  display: 'block',
  width: 30,
  height: 30,
  transition: 'transform 300ms',
})

const expandIconClosedStyle = css({
  transform: 'rotate(90deg)',
})

const expandIconOpenStyle = css({
  transform: 'rotate(-90deg)',
})

const detailCellStyle = css({
  padding: '16px 8px 24px 32px',
  background: '#F5F5F5',
})

// td の height を指定すると、子の height: 100% が行の高さに解決される（テーブルセルの定番の書き方）
const linkCellStyle = css({
  padding: 0,
  height: 1,
})

// reset.css が body の line-height を 1 にしており、部屋が複数あるときや文字が折り返したときに行どうしが詰まる。
// 宿泊者名など既存の表の複数行の表示と同じ 1.5 を、遷移するセルの中身すべてに効かせる
const cellLinkStyle = css({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  padding: 8,
  lineHeight: 1.5,
  color: '#272727',
  '&:hover': { color: '#272727' },
})

// flex の子は中身の幅より縮まないため、列幅まで縮められるようにする（宿泊者名の省略と変更内容の折り返しに必要）
const cellContentStyle = css({
  minWidth: 0,
  overflowWrap: 'anywhere',
})

const guestNameTextStyle = css({
  paddingRight: 8,
})

const summaryStyle = css({
  whiteSpace: 'pre-wrap',
})
